import * as THREE from 'three';
import type { CityMap } from '../game/map';
import type { AnimatedModel } from './CharacterModel';
import type { OutfitName } from './outfits';
import { createHeroineFigure, type Figure } from './Figure';

export const GAIT_NAMES = ['Stopped', 'Slow', 'Walking', 'Brisk'] as const;
// Unhurried, realistic paces (m/s). There is no running in this game.
const GAIT_SPEEDS = [0, 1.2, 1.9, 2.6];
const CAPPED_SPEED = 1.2;
const TEXTING_SPEED = 0.6;
const DARK_SPEED = 1.25;

export type HeroineControl = {
  steer: number; // -1 left .. +1 right
  lookBack: boolean;
  phone: boolean;
  /** Typing a reply: she barely moves. */
  texting?: boolean;
  /** Phone flashlight on: held out in front, lighting the path. */
  flashlight?: boolean;
  /** In the dark without a light: she picks her way slowly. */
  darkSlow?: boolean;
  /** Extra speed cap (m/s), e.g. staggering after being bumped. */
  maxSpeed?: number;
  headYaw: number;
};

export function wrapAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

export class Heroine {
  readonly figure: Figure;
  readonly group: THREE.Group;
  readonly forward = new THREE.Vector3(0, 0, -1);
  private readonly phoneScreen: THREE.MeshBasicMaterial;
  /**
   * Glow of her phone screen. Lives in `sceneLights` (added straight to the scene,
   * always visible) so the scene's light count never changes; changing it makes
   * three.js recompile every shader, a visible freeze.
   */
  private readonly phoneLight = new THREE.PointLight('#bcd8ff', 0, 2.2, 2);
  readonly sceneLights = new THREE.Group();
  heading = 0;
  speed = 0;
  gait = 2;
  phase = 0;
  private stepCount = 0;
  private headYaw = 0;
  private phoneBlend = 0;
  private torchBlend = 0;
  /** Phone flashlight. Always in the scene (intensity 0 when off) so light counts never change. */
  private readonly torch = new THREE.SpotLight('#eef3ff', 0, 17, 0.5, 0.55, 1.4);
  private readonly torchLed: THREE.Mesh;
  private model: AnimatedModel | null = null;
  private modelProp: THREE.Group | null = null;
  /** Clear vinyl umbrella in her left hand on rainy days. Origin is the grip. */
  private readonly umbrella = createUmbrella();
  private umbrellaOn = false;
  private outfit: OutfitName = 'uniform';
  /** Under a roof (the covered arcade): umbrella folded and lowered. */
  private sheltered = false;
  /** 0 folded at her side .. 1 open overhead. */
  private umbrellaBlend = 0;
  private readonly tmpHand = new THREE.Vector3();
  private readonly tmpHead = new THREE.Vector3();
  private readonly tmpX = new THREE.Vector3();
  private readonly tmpY = new THREE.Vector3();
  private readonly tmpZ = new THREE.Vector3();
  private readonly tmpM = new THREE.Matrix4();
  private readonly tmpQ = new THREE.Quaternion();
  /** Fired every footfall. */
  onStep: ((running: boolean) => void) | null = null;

  constructor() {
    const { figure, phoneScreen } = createHeroineFigure();
    this.figure = figure;
    this.group = figure.root;
    this.phoneScreen = phoneScreen;
    this.sceneLights.add(this.phoneLight);

    // Held at chest height, slightly right, aimed at the ground ahead.
    this.torch.position.set(0.22, 1.12, -0.32);
    this.torch.target.position.set(0.1, 0, -8);
    this.torch.castShadow = false;
    this.group.add(this.torch, this.torch.target);
    const ledMat = new THREE.MeshBasicMaterial({ color: '#ffffff' });
    ledMat.color.multiplyScalar(4);
    this.torchLed = new THREE.Mesh(new THREE.SphereGeometry(0.012, 8, 6), ledMat);
    this.torchLed.position.set(0.02, 0.05, -0.012);
    figure.propHand.add(this.torchLed);
    this.umbrella.visible = false;
    this.group.add(this.umbrella);
  }

  /** What she's wearing today (applied when the model loads, if it hasn't yet). */
  setOutfit(name: OutfitName): void {
    this.outfit = name;
    this.model?.setOutfit?.(name);
  }

  /** Rainy days: she carries an open umbrella. */
  setUmbrella(on: boolean): void {
    this.umbrellaOn = on;
    this.umbrellaBlend = on && !this.sheltered ? 1 : 0;
    this.umbrella.visible = on;
  }

  /** 0..1 how open her umbrella is (0 without one). */
  get umbrellaOpen(): number {
    return this.umbrellaOn ? this.umbrellaBlend : 0;
  }

  /** Under cover she folds the umbrella; back in the rain she opens it again. */
  setSheltered(sheltered: boolean): void {
    this.sheltered = sheltered;
  }

  /** Hold the umbrella up in her left hand, canopy over her head. */
  private updateUmbrella(dt: number): void {
    const open = this.umbrellaOn && !this.sheltered ? 1 : 0;
    this.umbrellaBlend += (open - this.umbrellaBlend) * Math.min(1, dt * 4);
    const b = this.umbrellaBlend;
    this.model?.holdLeft?.(b);
    this.umbrella.visible = this.umbrellaOn;
    if (!this.umbrellaOn) return;
    if (this.model?.leftPalmWorld) {
      this.umbrella.position.copy(this.group.worldToLocal(this.model.leftPalmWorld(this.tmpHand)));
    } else {
      this.umbrella.position.set(-0.12 - (1 - b) * 0.08, 0.8 + b * 0.3, -0.28 * b);
    }
    // Open: shaft leaning back over her shoulder, swaying a little as she walks.
    // Folded: carried by the handle, tip angled down behind her, swinging with her stride.
    const sway = Math.sin(this.phase) * Math.min(1, this.speed) * (0.03 + (1 - b) * 0.12);
    this.umbrella.rotation.set(THREE.MathUtils.lerp(Math.PI - 0.85, 0.24, b), 0, THREE.MathUtils.lerp(0, -0.06, b) + sway);
    // The canopy closes up along the shaft.
    const canopy = this.umbrella.userData.canopy as THREE.Object3D;
    const r = THREE.MathUtils.lerp(0.09, 1, b * b);
    canopy.scale.set(r, THREE.MathUtils.lerp(1.6, 1, b), r);
  }

  /** Swap the procedural figure for a skinned, animated model. */
  attachModel(model: AnimatedModel): void {
    if (this.model === model) return;
    // Changing clothes between nights: the old model stays loaded (the game caches it).
    if (this.model) this.group.remove(this.model.root);
    this.model = model;
    this.figure.body.visible = false;
    this.group.add(model.root);
    model.setOutfit?.(this.outfit);
    model.resetPose();
    if (this.modelProp) return;
    // Her phone, held in the model's right hand (positioned every frame).
    const prop = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.15, 0.012), new THREE.MeshStandardMaterial({ color: '#111', roughness: 0.4 }));
    const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.066, 0.13), this.phoneScreen);
    screen.position.z = 0.007;
    prop.add(body, screen, this.torchLed);
    this.torchLed.position.set(0.025, 0.06, -0.008);
    prop.visible = false;
    this.modelProp = prop;
    this.group.add(prop);
  }

  /** Standing still (title screen, endings). */
  animateIdle(dt: number, headYaw: number): void {
    this.figure.pose(0, 0, 0, 1, headYaw, 0);
    if (!this.model) return;
    this.model.locomotion(0, GAIT_SPEEDS[2], GAIT_SPEEDS[3], Infinity);
    this.model.holdLeft?.(this.umbrellaBlend);
    this.model.update(dt);
    this.updateUmbrella(dt);
    this.model.turnHead(headYaw);
  }

  /** Fear shows on her face (VRM models). */
  setFear(t: number): void {
    this.model?.setFear?.(t);
  }

  /** She flinches when grabbed. */
  flinch(): void {
    this.model?.playOnce('HitRecieve', 0.1);
  }

  /** Battery died: the light cuts out instantly. */
  killFlashlight(): void {
    this.torchBlend = 0;
    this.torch.intensity = 0;
    this.torchLed.visible = false;
    this.figure.propHand.visible = false;
    if (this.modelProp) this.modelProp.visible = false;
    // The phone goes down too: no screen glow.
    this.phoneBlend = 0;
    this.phoneLight.intensity = 0;
  }

  /** 0..1 how much the flashlight is on (for light/exposure checks). */
  get flashlightLevel(): number {
    return this.torchBlend;
  }

  get position(): THREE.Vector3 {
    return this.group.position;
  }

  /** True once a skinned model (VRM or glTF) is attached. */
  get hasModel(): boolean {
    return this.model !== null;
  }

  reset(x: number, z: number, heading: number): void {
    this.group.position.set(x, 0, z);
    this.heading = heading;
    this.speed = 0;
    this.gait = 2;
    this.phase = 0;
    this.stepCount = 0;
    this.headYaw = 0;
    this.phoneBlend = 0;
    this.torchBlend = 0;
    this.torch.intensity = 0;
    this.group.rotation.y = heading;
    this.updateForward();
    this.figure.pose(0, 0, 0, 1, 0);
    this.model?.resetPose();
    if (this.modelProp) this.modelProp.visible = false;
  }

  shiftGait(delta: number): void {
    this.gait = THREE.MathUtils.clamp(this.gait + delta, 0, GAIT_SPEEDS.length - 1);
  }

  setGait(g: number): void {
    this.gait = THREE.MathUtils.clamp(Math.round(g), 0, GAIT_SPEEDS.length - 1);
  }

  get gaitLabel(): string {
    return GAIT_NAMES[this.gait];
  }

  update(dt: number, ctrl: HeroineControl, map: CityMap, frozen: boolean): void {
    // --- speed
    let target = GAIT_SPEEDS[this.gait];
    if (ctrl.lookBack || ctrl.phone) target = Math.min(target, CAPPED_SPEED);
    if (ctrl.texting) target = Math.min(target, TEXTING_SPEED);
    if (ctrl.darkSlow) target = Math.min(target, DARK_SPEED);
    if (ctrl.maxSpeed !== undefined) target = Math.min(target, ctrl.maxSpeed);
    if (frozen) target = 0;
    const accel = target > this.speed ? 2.4 : 4.5;
    this.speed += THREE.MathUtils.clamp(target - this.speed, -accel * dt, accel * dt);

    // --- steering
    if (!frozen) {
      const turnRate = 2.2;
      this.heading = wrapAngle(this.heading - ctrl.steer * turnRate * dt);
      if (Math.abs(ctrl.steer) < 0.05 && this.speed > 0.3) {
        // Gently settle onto the street's axis so walking straight is effortless.
        const snap = Math.round(this.heading / (Math.PI / 2)) * (Math.PI / 2);
        const diff = wrapAngle(snap - this.heading);
        if (Math.abs(diff) < 0.55) this.heading += diff * Math.min(1, dt * 2.6);
      }
    }
    this.group.rotation.y = this.heading;
    this.updateForward();

    // --- movement with wall sliding
    if (this.speed > 0.001) {
      const p = this.group.position;
      const dx = this.forward.x * this.speed * dt;
      const dz = this.forward.z * this.speed * dt;
      if (map.isWalkable(p.x + dx, p.z + dz)) {
        p.x += dx;
        p.z += dz;
      } else if (map.isWalkable(p.x + dx, p.z)) {
        p.x += dx;
        this.speed *= 1 - Math.min(1, dt * 1.5);
      } else if (map.isWalkable(p.x, p.z + dz)) {
        p.z += dz;
        this.speed *= 1 - Math.min(1, dt * 1.5);
      } else {
        this.speed *= 1 - Math.min(1, dt * 8);
      }
    }

    // --- animation
    const stride = Math.min(1.15, this.speed / GAIT_SPEEDS[2]);
    const runBlend = 0; // no running
    this.phase += this.speed * dt * (2.7 - runBlend * 0.7);
    const steps = Math.floor(this.phase / Math.PI);
    if (steps !== this.stepCount) {
      this.stepCount = steps;
      if (this.speed > 0.4) this.onStep?.(runBlend > 0.4);
    }
    this.headYaw += (THREE.MathUtils.clamp(ctrl.headYaw, -1.25, 1.25) - this.headYaw) * Math.min(1, dt * 6);
    this.phoneBlend += ((ctrl.phone ? 1 : 0) - this.phoneBlend) * Math.min(1, dt * 7);
    this.figure.pose(this.phase, stride, runBlend, 1 - this.phoneBlend, this.headYaw, this.phoneBlend * 0.45);
    if (this.phoneBlend > 0.02) {
      this.figure.armR.rotation.x = THREE.MathUtils.lerp(this.figure.armR.rotation.x, 1.25, this.phoneBlend);
      this.figure.armR.rotation.z = -0.35 * this.phoneBlend;
    } else {
      this.figure.armR.rotation.z = 0;
    }
    // Flashlight: arm held out lower and forward, phone back facing the street.
    this.torchBlend += ((ctrl.flashlight ? 1 : 0) - this.torchBlend) * Math.min(1, dt * 9);
    const torchArm = this.torchBlend * (1 - this.phoneBlend);
    if (torchArm > 0.02) {
      this.figure.armR.rotation.x = THREE.MathUtils.lerp(this.figure.armR.rotation.x, 0.85, torchArm);
      this.figure.armR.rotation.z = -0.2 * torchArm;
    }
    this.torch.intensity = this.torchBlend * 26;
    this.torchLed.visible = this.torchBlend > 0.5;
    this.figure.propHand.visible = this.phoneBlend > 0.3 || this.torchBlend > 0.3;
    this.phoneLight.intensity = this.phoneBlend * 0.06;
    this.placePhoneLight();
    if (!this.model) this.updateUmbrella(dt);

    if (this.model && this.modelProp) {
      const m = this.model;
      m.locomotion(this.speed, GAIT_SPEEDS[2], GAIT_SPEEDS[3], Infinity);
      m.holdLeft?.(this.umbrellaBlend);
      m.update(dt);
      m.turnHead(this.headYaw, this.phoneBlend * 0.45);
      const hold = Math.max(this.phoneBlend, torchArm);
      m.raiseRightHand(hold, torchArm > this.phoneBlend ? 1 : 0);
      // Phone sits in her hand, screen turned toward her face.
      const prop = this.modelProp;
      prop.visible = this.phoneBlend > 0.3 || this.torchBlend > 0.3;
      if (prop.visible) this.placeProp(m, prop);
      this.updateUmbrella(dt);
    }
  }

  /**
   * Seat the phone in her palm: upright, screen turned toward her eyes, the
   * bottom resting in the heel of the hand.
   */
  private placeProp(m: AnimatedModel, prop: THREE.Group): void {
    const palm = m.palmWorld ? m.palmWorld(this.tmpHand) : m.handWorld(this.tmpHand);
    const z = this.tmpZ.copy(m.headWorld(this.tmpHead)).sub(palm).normalize();
    // Portrait: the long edge stands roughly upright, leaning a little with the forearm.
    const y = m.forearmDir ? m.forearmDir(this.tmpY).multiplyScalar(0.35) : this.tmpY.set(0, 0, 0);
    y.y += 1;
    y.addScaledVector(z, -y.dot(z)).normalize();
    const x = this.tmpX.crossVectors(y, z);
    this.tmpM.makeBasis(x, y, z);
    this.tmpQ.setFromRotationMatrix(this.tmpM);
    this.group.getWorldQuaternion(prop.quaternion).invert().multiply(this.tmpQ);
    // Phone centre: a little up its long axis from the palm, resting on the skin.
    const centre = palm.addScaledVector(y, 0.035).addScaledVector(z, 0.012);
    prop.position.copy(this.group.worldToLocal(centre));
    this.torch.position.copy(prop.position);
  }

  /** Keep the screen glow at her hand (world space). */
  private placePhoneLight(): void {
    if (this.phoneLight.intensity <= 0) return;
    // Between the screen and her face: the glow lights her face, not a hot spot on her hand.
    const holder = this.modelProp ?? this.figure.propHand;
    holder.getWorldPosition(this.phoneLight.position);
    // Just in front of the screen, toward her face: a soft blue wash, not a spotlight.
    if (this.model) this.phoneLight.position.lerp(this.model.headWorld(this.tmpHead), 0.25);
    else this.phoneLight.position.y += 0.08;
  }

  private updateForward(): void {
    this.forward.set(-Math.sin(this.heading), 0, -Math.cos(this.heading));
  }

  dispose(): void {
    this.figure.dispose();
    this.phoneScreen.dispose();
    this.model?.dispose();
    this.torchLed.geometry.dispose();
    (this.torchLed.material as THREE.Material).dispose();
    this.umbrella.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    });
  }
}

/** A clear vinyl konbini umbrella, open. Origin at the grip; the shaft points up +Y. */
function createUmbrella(): THREE.Group {
  const g = new THREE.Group();
  const frame = new THREE.MeshStandardMaterial({ color: '#c9ced2', roughness: 0.35, metalness: 0.6 });
  const handle = new THREE.MeshStandardMaterial({ color: '#ecebe4', roughness: 0.5 });
  const vinyl = new THREE.MeshStandardMaterial({
    color: '#e6eef3',
    roughness: 0.12,
    transparent: true,
    opacity: 0.26,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const shaftTop = 0.66;
  const radius = 0.5;
  const drop = 0.2;
  const ribs = 8;
  // Grip and J-hook below the hand.
  const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.013, 0.013, 0.12, 8), handle);
  grip.position.y = -0.02;
  const hook = new THREE.Mesh(new THREE.TorusGeometry(0.035, 0.011, 6, 12, Math.PI), handle);
  hook.position.set(0.035, -0.08, 0);
  hook.rotation.z = Math.PI;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, shaftTop + 0.08, 6), frame);
  shaft.position.y = (shaftTop + 0.08) / 2 - 0.02;
  // Canopy: an open eight-panel cone hanging from the top of the shaft. Its group
  // pivots at the apex so scaling it folds the umbrella closed along the shaft.
  const canopy = new THREE.Group();
  canopy.position.y = shaftTop;
  const cloth = new THREE.Mesh(new THREE.ConeGeometry(radius, drop, ribs, 1, true), vinyl);
  cloth.position.y = -drop / 2;
  cloth.renderOrder = 2;
  canopy.add(cloth);
  const tip = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.004, 0.06, 6), frame);
  tip.position.y = shaftTop + 0.03;
  g.add(grip, hook, shaft, canopy, tip);
  g.userData.canopy = canopy;
  // Ribs along each panel seam, out to the rim.
  const ribLen = Math.hypot(radius, drop);
  for (let i = 0; i < ribs; i += 1) {
    const a = (i / ribs) * Math.PI * 2;
    const rib = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.006, ribLen), frame);
    rib.position.set((Math.sin(a) * radius) / 2, -drop / 2, (Math.cos(a) * radius) / 2);
    rib.rotation.set(Math.atan2(drop, radius), 0, 0);
    rib.rotation.order = 'YXZ';
    rib.rotation.y = a;
    canopy.add(rib);
  }
  g.traverse((o) => {
    (o as THREE.Mesh).castShadow = true;
  });
  return g;
}
